import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getTrafficSourceString, clearTrafficSource } from "@/hooks/useTrafficSource";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscribed: boolean;
  subscriptionEnd: string | null;
  checkingSubscription: boolean;
  inTrial: boolean;
  trialExpired: boolean;
  trialDaysRemaining: number;
  hasAccess: boolean; // true if subscribed OR in trial
  hasCheckedOnce: boolean; // true once subscription has been checked at least once
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  checkSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [inTrial, setInTrial] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [lastCheckTime, setLastCheckTime] = useState<number>(0);
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // User has access if they're subscribed OR in their trial period
  const hasAccess = subscribed || inTrial;

  const checkSubscription = async (currentSession?: Session | null, retryCount = 0, forceCheck = false) => {
    const sessionToUse = currentSession ?? session;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 500; // ms
    const MIN_CHECK_INTERVAL = 30000; // Don't re-check within 30 seconds unless forced
    
    // Skip check if we've checked recently (prevents loading flash on tab focus)
    const now = Date.now();
    if (!forceCheck && hasCheckedOnce && (now - lastCheckTime) < MIN_CHECK_INTERVAL) {
      return;
    }
    
    if (!sessionToUse) {
      setSubscribed(false);
      setSubscriptionEnd(null);
      setInTrial(false);
      setTrialExpired(false);
      setTrialDaysRemaining(0);
      setCheckingSubscription(false);
      setHasCheckedOnce(true);
      return;
    }

    // Only show loading state if we haven't checked before
    if (!hasCheckedOnce) {
      setCheckingSubscription(true);
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.error('Error checking subscription:', error);
        // Retry on failure
        if (retryCount < MAX_RETRIES) {
          console.log(`Retrying subscription check (${retryCount + 1}/${MAX_RETRIES})...`);
          setTimeout(() => {
            checkSubscription(currentSession, retryCount + 1);
          }, RETRY_DELAY * (retryCount + 1)); // Exponential backoff
          return;
        }
        setCheckingSubscription(false);
        return;
      }

      const isSubscribed = data?.subscribed ?? false;
      const endDate = data?.subscription_end ?? null;
      const isInTrial = data?.in_trial ?? false;
      const isTrialExpired = data?.trial_expired ?? false;
      const daysRemaining = data?.trial_days_remaining ?? 0;
      
      console.log('Subscription check result:', { 
        isSubscribed, 
        endDate, 
        isInTrial, 
        isTrialExpired, 
        daysRemaining 
      });
      
      setSubscribed(isSubscribed);
      setSubscriptionEnd(endDate);
      setInTrial(isInTrial);
      setTrialExpired(isTrialExpired);
      setTrialDaysRemaining(daysRemaining);
      setCheckingSubscription(false);
      setLastCheckTime(Date.now());
      setHasCheckedOnce(true);
    } catch (err) {
      console.error('Error checking subscription:', err);
      // Retry on exception
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying subscription check (${retryCount + 1}/${MAX_RETRIES})...`);
        setTimeout(() => {
          checkSubscription(currentSession, retryCount + 1, true);
        }, RETRY_DELAY * (retryCount + 1));
        return;
      }
      setCheckingSubscription(false);
    }
  };

  // Identify user in Microsoft Clarity for session tracking
  const identifyUserInClarity = (userEmail: string, userId: string) => {
    if (typeof window !== 'undefined' && (window as any).clarity) {
      (window as any).clarity("identify", userId, undefined, undefined, userEmail);
      console.log('Clarity user identified:', userEmail);
    }
  };

  // Clear Clarity identification on sign out
  const clearClarityIdentification = () => {
    if (typeof window !== 'undefined' && (window as any).clarity) {
      (window as any).clarity("identify", null);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
        
        // Only check subscription on actual auth events, not on every state change
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Identify user in Clarity
          if (newSession?.user?.email) {
            identifyUserInClarity(newSession.user.email, newSession.user.id);
          }
          
          if (newSession && !hasCheckedOnce) {
            setCheckingSubscription(true);
            setTimeout(() => {
              checkSubscription(newSession, 0, true);
            }, 100);
          }
        } else if (event === 'SIGNED_OUT') {
          clearClarityIdentification();
          setSubscribed(false);
          setSubscriptionEnd(null);
          setInTrial(false);
          setTrialExpired(false);
          setTrialDaysRemaining(0);
          setCheckingSubscription(false);
          setHasCheckedOnce(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
      
      // Identify user in Clarity on initial load
      if (initialSession?.user?.email) {
        identifyUserInClarity(initialSession.user.email, initialSession.user.id);
      }
      
      if (initialSession && !hasCheckedOnce) {
        setCheckingSubscription(true);
        // Add a small delay to ensure session is fully propagated
        setTimeout(() => {
          checkSubscription(initialSession, 0, true);
        }, 100);
      } else if (!initialSession) {
        setCheckingSubscription(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Real-time listener for club membership changes (instant access grant/revocation)
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('membership-changes')
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'club_memberships',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Club membership removed (kicked or left), rechecking access...', payload);
          // Immediately recheck subscription when membership is removed
          checkSubscription(undefined, 0, true);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'club_memberships',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Club membership added, rechecking access...', payload);
          // Immediately recheck subscription when joining a club
          checkSubscription(undefined, 0, true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Periodically check subscription status every 5 minutes (reduced from 1 minute for rate limiting)
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      checkSubscription();
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [session]);

  const signUp = async (email: string, password: string, displayName?: string) => {
    // Capture traffic source before signup
    const trafficSource = getTrafficSourceString();
    const referrer = document.referrer || null;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: { display_name: displayName },
      },
    });
    
    // If signup successful, update the profile with traffic source
    if (!error && data.user) {
      // Small delay to ensure profile trigger has created the row
      setTimeout(async () => {
        try {
          await supabase
            .from('profiles')
            .update({ 
              traffic_source: trafficSource,
              signup_referrer: referrer
            })
            .eq('user_id', data.user!.id);
          
          clearTrafficSource();
        } catch (err) {
          console.error('Failed to save traffic source:', err);
        }
      }, 500);
    }
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    setUser(null);
    setSession(null);
    setSubscribed(false);
    setSubscriptionEnd(null);
    setInTrial(false);
    setTrialExpired(false);
    setTrialDaysRemaining(0);
    
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.log('Sign out completed (session may have already expired)');
    }
  };

  const value = { 
    user, 
    session, 
    loading, 
    subscribed, 
    subscriptionEnd, 
    checkingSubscription,
    inTrial,
    trialExpired,
    trialDaysRemaining,
    hasAccess,
    hasCheckedOnce,
    signUp, 
    signIn, 
    signOut,
    checkSubscription
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
